import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { errMsg, useApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Empty, LEAD_ST, LEAD_TONE, Pill, canWrite, crmErr, toCents, when } from "./helpers";
import LeadDetail, { leadMessage } from "./LeadDetail";

function ConvertDialog({ lead, onClose, onDone }) {
  const [title, setTitle] = useState("");
  const [value, setValue] = useState("");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="lead-convert-dialog">
        <DialogHeader><DialogTitle>Converti «{lead.name}» in contatto</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Il contatto viene creato con l'email <b>{lead.email}</b> (o riusato se esiste già). Puoi aprire subito un'opportunità.</p>
        {leadMessage(lead) && <blockquote className="whitespace-pre-wrap rounded-md border-l-4 border-sky-300 bg-slate-50 px-3 py-2 text-sm text-slate-700" data-testid="lead-convert-message">{leadMessage(lead)}</blockquote>}
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="sm:col-span-2"><Label className="text-xs">Opportunità (facoltativa)</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Es. Preventivo evento" data-testid="lead-convert-deal-title" /></div>
          <div><Label className="text-xs">Valore €</Label><Input value={value} onChange={(e) => setValue(e.target.value)} inputMode="decimal" data-testid="lead-convert-deal-value" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={() => onDone({ deal_title: title.trim(), deal_value_cents: toCents(value) })} data-testid="lead-convert-confirm">Converti</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LeadCard({ l, role, act, onConvert, onOpen }) {
  const open = ["new", "working"].includes(l.status);
  const msg = leadMessage(l);
  const stop = (e) => e.stopPropagation();
  return (
    <div role="button" tabIndex={0} onClick={() => onOpen(l)} onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) { e.preventDefault(); onOpen(l); } }}
      aria-label={`Apri la richiesta di ${l.name || "senza nome"}`}
      className="min-w-0 cursor-pointer rounded-lg border border-slate-200 bg-white p-4 transition-[border-color,box-shadow] hover:border-sky-300 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500" data-testid={`lead-${l.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0"><p className="truncate text-sm font-semibold text-slate-900">{l.name || "Senza nome"}</p>
          <p className="truncate text-xs text-slate-500">{l.email || "nessuna email"} · {l.form_name || "modulo"} · {when(l.created_at)}</p></div>
        <div className="flex flex-wrap gap-1"><Pill tone={LEAD_TONE[l.status]} testid={`lead-status-${l.id}`}>{LEAD_ST[l.status]}</Pill>
          {l.duplicate_of && <Pill tone="bg-violet-50 text-violet-700" testid={`lead-dup-${l.id}`}>Duplicato</Pill>}
          {l.contact_id && l.status !== "converted" && <Pill tone="bg-slate-100 text-slate-600">Contatto già presente</Pill>}
          {l.is_test && <Pill tone="bg-slate-100 text-slate-500">Prova</Pill>}</div>
      </div>
      {msg ? <p className="mt-2 line-clamp-2 text-sm text-slate-700" data-testid={`lead-message-${l.id}`}>«{msg}»</p> : l.summary && <p className="mt-2 line-clamp-3 text-xs text-slate-600">{l.summary}</p>}
      <p className="mt-1 text-[11px] text-sky-700">Apri il dettaglio</p>
      {open && canWrite(role) && <div className="mt-3 flex flex-wrap gap-2" onClick={stop} onKeyDown={stop}>
        {l.status === "new" && <Button size="sm" variant="outline" onClick={() => act(l, "working")} data-testid={`lead-working-${l.id}`}>Prendi in carico</Button>}
        <Button size="sm" onClick={() => onConvert(l)} disabled={!l.email} data-testid={`lead-convert-${l.id}`}>Converti</Button>
        <Button size="sm" variant="ghost" onClick={() => act(l, "discard")} data-testid={`lead-discard-${l.id}`}>Scarta</Button>
        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => act(l, "spam")} data-testid={`lead-spam-${l.id}`}>Spam</Button></div>}
    </div>
  );
}

export default function CrmLeads({ tid, role, onChanged }) {
  const api = useApi();
  const [status, setStatus] = useState("open");
  const [list, setList] = useState(null);
  const [conv, setConv] = useState(null);
  const [detail, setDetail] = useState(null);
  const load = useCallback(() => api.get(`/t/${tid}/crm/leads`, { params: { status: status === "open" ? "" : status } }).then(({ data }) => setList(data))
    .catch((e) => { setList([]); toast.error(errMsg(e)); }), [api, tid, status]);
  useEffect(() => { load(); }, [load]);
  const act = async (l, action, extra = {}) => {
    try { await api.post(`/t/${tid}/crm/leads/${l.id}/action`, { action, ...extra }); toast.success(action === "convert" ? "Richiesta convertita in contatto" : "Richiesta aggiornata"); setConv(null); load(); onChanged?.(); }
    catch (e) { toast.error(crmErr(e, errMsg)); }
  };
  return (
    <div className="space-y-3" data-testid="crm-leads">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="h-9 w-48 bg-white text-sm" data-testid="lead-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="open">Da gestire</SelectItem>{Object.entries(LEAD_ST).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
        <p className="text-xs text-slate-500">Le richieste arrivano dai moduli dei siti del cliente. Lo spam resta fuori dalla casella.</p>
      </div>
      {list === null ? <p className="text-sm text-slate-500">Caricamento…</p> : !list.length ? <Empty testid="leads-empty">Nessuna richiesta in questo stato.</Empty> :
        <div className="grid gap-3 lg:grid-cols-2">{list.map((l) => <LeadCard key={l.id} l={l} role={role} act={act} onConvert={setConv} onOpen={setDetail} />)}</div>}
      {detail && <LeadDetail tid={tid} lead={detail} role={role} onClose={() => setDetail(null)} act={act} onConvert={setConv} />}
      {conv && <ConvertDialog lead={conv} onClose={() => setConv(null)} onDone={(x) => act(conv, "convert", x)} />}
    </div>
  );
}
